import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft } from 'react-icons/fa';
import { projectsData } from '../data/projectsData';
import { githubProjectsData } from '../data/githubProjectsData';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';

const ProjectDetails = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const proj = projectsData.find(p => p.id === id) || githubProjectsData.find(p => p.id === id);
    setProject(proj);
  }, [id]);

  if (!project) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <h2 className="text-gradient">Project Not Found</h2>
      </div>
    );
  }

  const { metrics = {}, radar = [], tech = [], outcomes = [] } = project;

  return (
    <div className="container" style={{ paddingTop: '100px', paddingBottom: '100px', minHeight: '100vh' }}>
      {githubProjectsData.some(p => p.id === id) ? (
        <Link to="/github-projects" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--accent-cyan)', marginBottom: '2rem', textDecoration: 'none', fontWeight: 600 }}>
          <FaArrowLeft /> Back to Github Projects
        </Link>
      ) : (
        <Link to="/projects" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--accent-cyan)', marginBottom: '2rem', textDecoration: 'none', fontWeight: 600 }}>
          <FaArrowLeft /> Back to All Projects
        </Link>
      )}

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="glass-panel project-document"
        style={{ padding: 0, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(10, 15, 30, 0.8)' }}
      >
        {/* Header Section */}
        <div style={{ padding: '2rem 3rem', background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h1 style={{ fontSize: 'clamp(2rem, 4vw, 2.5rem)', marginBottom: '0.5rem', color: 'white' }}>
                {project.title} <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>- {project.shortDesc}</span>
              </h1>
              <p style={{ color: 'var(--accent-cyan)', fontSize: '1.1rem', fontStyle: 'italic', letterSpacing: '0.5px' }}>
                {project.programType} <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span> {project.role}
              </p>
            </div>
            {project.link && (
              <a href={project.link} target="_blank" rel="noreferrer" className="contact-btn" style={{ padding: '10px 20px', fontSize: '0.9rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', marginTop: '5px' }}>
                Launch Live Demo ↗
              </a>
            )}
          </div>
        </div>

        {/* Impact Bar */}
        <div style={{ padding: '1.5rem 3rem', background: 'linear-gradient(90deg, #1A365D 0%, #0F203D 100%)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <h3 style={{ color: 'white', fontSize: '1.2rem', fontWeight: 600, lineHeight: 1.6 }}>
            <span style={{ color: '#63B3ED', marginRight: '10px' }}>KEY IMPACT</span> 
            <span style={{ fontWeight: 400 }}>{project.impact || project.desc}</span>
          </h3>
        </div>

        {/* 4-Stat Metrics Bar */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: '1px solid rgba(255,255,255,0.1)', textAlign: 'center', background: 'rgba(0,0,0,0.2)' }}>
          {['Ownership', 'Scale', 'Architecture', 'Modern Tech'].map((stat, i) => {
            const key = stat === 'Modern Tech' ? 'modernTech' : stat.toLowerCase();
            return (
              <div key={i} style={{ padding: '1.5rem', borderRight: i !== 3 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                <p style={{ color: '#4299E1', fontSize: '0.9rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.5rem', letterSpacing: '1px' }}>{stat}</p>
                <p style={{ color: 'white', fontSize: '1.1rem' }}>
                  {metrics[key] || '-'}
                </p>
              </div>
            );
          })}
        </div>

        {/* Main 2-Column Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0', background: 'transparent' }}>
          
          {/* Left Column */}
          <div style={{ padding: '3rem', borderRight: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ marginBottom: '3rem' }}>
              <h2 style={{ fontSize: '1.5rem', color: '#63B3ED', marginBottom: '1rem' }}>Overview</h2>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1.5rem' }}>
                {project.overview || project.desc}
              </p>
              {project.scaleComplexity && (
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                  <strong style={{ color: 'white' }}>Scale & complexity:</strong> {project.scaleComplexity}
                </p>
              )}
            </div>

            <div>
              <h2 style={{ fontSize: '1.5rem', color: '#63B3ED', marginBottom: '1rem' }}>Key contributions</h2>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', color: 'var(--text-secondary)' }}>
                {outcomes.map((outcome, i) => (
                  <li key={i} style={{ marginBottom: '0.8rem', lineHeight: 1.6 }}>{outcome}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Column */}
          <div style={{ padding: '3rem' }}>
            <div style={{ marginBottom: '3rem' }}>
              <h2 style={{ fontSize: '1.5rem', color: '#63B3ED', marginBottom: '1rem' }}>Architecture & technology</h2>
              {project.architectureTech && (
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1.5rem' }}>
                  {project.architectureTech}
                </p>
              )}
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                <strong style={{ color: 'white' }}>Tech stack:</strong> {tech.join(', ')}
              </p>
            </div>

            <div style={{ marginBottom: '3rem' }}>
              <h2 style={{ fontSize: '1.5rem', color: '#63B3ED', marginBottom: '1rem' }}>Business outcome & Leadership DNA</h2>
              {project.businessOutcome && (
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
                  {project.businessOutcome}
                </p>
              )}
              {project.trendingTechLens && (
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                  <strong style={{ color: 'white' }}>Trending-tech lens:</strong> {project.trendingTechLens}
                </p>
              )}
            </div>

            {/* Radar Chart */}
            {radar && radar.length > 0 && (
              <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <h3 style={{ fontSize: '1.3rem', color: 'white', marginBottom: '1rem' }}>Leadership DNA Snapshot</h3>
                <div style={{ width: '100%', height: 300, background: 'rgba(0,0,0,0.1)', borderRadius: '15px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radar}>
                      <PolarGrid stroke="rgba(255,255,255,0.1)" />
                      <PolarAngleAxis dataKey="subject" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(10,15,30,0.9)', border: '1px solid var(--accent-cyan)' }} />
                      <Radar name="Signal" dataKey="A" stroke="#63B3ED" fill="#63B3ED" fillOpacity={0.4} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

        </div>
      </motion.div>

      {/* Basic responsive handling for columns via global CSS rule injected quickly */}
      <style>{`
        @media (max-width: 900px) {
          .project-document > div:nth-child(4) {
            grid-template-columns: 1fr !important;
          }
          .project-document > div:nth-child(4) > div:first-child {
            border-right: none !important;
            border-bottom: 1px solid rgba(255,255,255,0.1);
          }
          .project-document > div:nth-child(3) {
            grid-template-columns: 1fr 1fr !important;
          }
          .project-document > div:nth-child(3) > div {
            border-right: 1px solid rgba(255,255,255,0.1) !important;
            border-bottom: 1px solid rgba(255,255,255,0.1) !important;
          }
          .project-document > div:nth-child(3) > div:nth-child(even) {
            border-right: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProjectDetails;
